import { FC, ReactNode } from 'react';
import {
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  type ButtonProps,
  IconButton,
  Breakpoint,
} from '@mui/material';
import { Column, Row } from '../Layout';
import { Close } from '@mui/icons-material';

type Props = {
  actions: {
    confirm: {
      onClick: () => void;
      label?: string;
      variant?: ButtonProps['variant'];
      color?: ButtonProps['color'];
    };
    alternateConfirm?: {
      onClick: () => void;
      label?: string;
      variant?: ButtonProps['variant'];
      color?: ButtonProps['color'];
    };
    cancel?: {
      onClick?: () => void;
      label?: string;
      variant?: ButtonProps['variant'];
      color?: ButtonProps['color'];
    };
  };
  open?: boolean;
  onClose?: () => void;
  title: string;
  content: ReactNode;
  loading?: boolean;
  maxWidth?: Breakpoint;
};

const ConfirmDialog: FC<Props> = ({
  open,
  onClose,
  actions,
  title,
  content,
  loading,
  maxWidth = 'xs',
}) => {
  const confirmAction = {
    label: 'Confirm',
    variant: 'contained',
    color: 'primary',
    ...actions.confirm,
  } as const;
  const cancelAction = {
    label: 'Cancel',
    variant: 'outlined',
    color: 'primary',
    onClick: onClose,
    ...(actions.cancel ?? {}),
  } as const;

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth={maxWidth} fullWidth>
      <DialogTitle>
        <Column spacing="sm" fullWidth>
          <Row centered fullWidth justify="space-between">
            <Typography variant="h4">{title}</Typography>
            <IconButton onClick={onClose} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Row>
          <Divider />
        </Column>
      </DialogTitle>

      <DialogContent>{content}</DialogContent>
      <DialogActions>
        <Button
          variant={cancelAction.variant}
          color={cancelAction.color}
          size="large"
          onClick={cancelAction.onClick}
          disabled={loading}
        >
          {cancelAction.label}
        </Button>
        <Row spacing="sm">
          {actions.alternateConfirm ? (
            <Button
              variant={actions.alternateConfirm.variant ?? 'outlined'}
              color={actions.alternateConfirm.color}
              size="large"
              onClick={actions.alternateConfirm.onClick}
              loading={loading}
            >
              {actions.alternateConfirm.label}
            </Button>
          ) : null}
          <Button
            variant={confirmAction.variant}
            color={confirmAction.color}
            size="large"
            onClick={confirmAction.onClick}
            loading={loading}
          >
            {confirmAction.label}
          </Button>
        </Row>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
